/**
 * procurementCvbbApi — RTK Query slice cho /purchase/cvbb/* (Công văn & Biên bản,
 * Access `frmProcurementMainSubFormCVBB`). List đọc VIEW public.v_congvan_bienban;
 * CRUD ghi base table public.congvan_bienban (soft delete is_status=1).
 * Tag types: 'ProcCvbb' (list) + 'ProcCvbbDetail' (single/form snapshot).
 */
import { baseApi } from './baseApi'

export interface CvbbListItem {
  cvbb_id: number
  cvbb_date: string | null
  cvbb_type: string | null
  cvbb_no: string | null
  contract_no: string | null
  client_short: string | null
  info_txt: string | null
  invoice_no: string | null
  note: string | null
  date_updated: string | null
  person_updated: string | null
  cvbb_vn: string | null
  cvbb_eng: string | null
  cvbb_full_no: string | null
}

export interface CvbbListResponse {
  items: CvbbListItem[]
  total: number
}

/** Snapshot cho dialog Edit/Copy — field của form Add + contract tách P/S. */
export interface CvbbFormSnapshot {
  cvbb_id: number
  cvbb_no: string | null
  cvbb_date: string | null
  cvbb_type: string | null
  p_contract: string | null
  s_contract: string | null
  info_txt: string | null
  invoice_no: string | null
  note: string | null
}

export interface CvbbWrite {
  cvbb_no: string | null
  cvbb_date: string | null
  cvbb_type: string | null
  /** contract_no đã resolve (P→p_contract, S→s_contract) ở FE trước khi gửi. */
  contract_no: string | null
  info_txt: string | null
  invoice_no: string | null
  note: string | null
}

export interface CvbbLookupItem {
  value: string
  label: string
}
export interface CvbbLookupResponse {
  items: CvbbLookupItem[]
  total: number
}
export interface CvbbDataResponse<T> {
  data: T
  message?: string
}
export interface CvbbMessageResponse {
  message: string
}

export const procurementCvbbApi = baseApi.injectEndpoints({
  endpoints: (b) => ({
    listCvbb: b.query<CvbbListResponse, { page?: number; pageSize?: number } | void>({
      query: (params) => ({ url: '/purchase/cvbb/list', params: params ?? {} }),
      providesTags: (res) =>
        res
          ? [
              ...res.items.map((it) => ({ type: 'ProcCvbb' as const, id: it.cvbb_id })),
              { type: 'ProcCvbb' as const, id: 'LIST' },
            ]
          : [{ type: 'ProcCvbb' as const, id: 'LIST' }],
    }),
    getCvbbForm: b.query<CvbbDataResponse<CvbbFormSnapshot>, number>({
      query: (id) => `/purchase/cvbb/${id}/form`,
      providesTags: (_r, _e, id) => [{ type: 'ProcCvbbDetail', id }],
      keepUnusedDataFor: 0,
    }),
    createCvbb: b.mutation<CvbbDataResponse<CvbbFormSnapshot>, CvbbWrite>({
      query: (body) => ({ url: '/purchase/cvbb/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'ProcCvbb', id: 'LIST' }],
    }),
    updateCvbb: b.mutation<CvbbDataResponse<CvbbFormSnapshot>, { id: number; body: CvbbWrite }>({
      query: ({ id, body }) => ({ url: `/purchase/cvbb/${id}`, method: 'PUT', body }),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'ProcCvbb', id: arg.id },
        { type: 'ProcCvbb', id: 'LIST' },
        { type: 'ProcCvbbDetail', id: arg.id },
      ],
    }),
    deleteCvbb: b.mutation<CvbbMessageResponse, number>({
      query: (id) => ({ url: `/purchase/cvbb/${id}`, method: 'DELETE' }),
      invalidatesTags: (_r, _e, id) => [
        { type: 'ProcCvbb', id },
        { type: 'ProcCvbb', id: 'LIST' },
      ],
    }),
    // Lookups
    cvbbPContractsLookup: b.query<CvbbLookupResponse, { q?: string } | void>({
      query: (arg) => ({ url: '/purchase/cvbb/lookups/p-contracts', params: arg && arg.q ? { q: arg.q } : {} }),
    }),
    cvbbSContractsLookup: b.query<CvbbLookupResponse, { q?: string } | void>({
      query: (arg) => ({ url: '/purchase/cvbb/lookups/s-contracts', params: arg && arg.q ? { q: arg.q } : {} }),
    }),
    cvbbTypesLookup: b.query<CvbbLookupResponse, void>({
      query: () => '/purchase/cvbb/lookups/cvbb-types',
    }),
    cvbbInvoicesLookup: b.query<CvbbLookupResponse, { contractNo: string }>({
      query: ({ contractNo }) => ({
        url: '/purchase/cvbb/lookups/invoices',
        params: { contractNo },
      }),
      keepUnusedDataFor: 0,
    }),
  }),
})

export const {
  useListCvbbQuery,
  useGetCvbbFormQuery,
  useLazyGetCvbbFormQuery,
  useCreateCvbbMutation,
  useUpdateCvbbMutation,
  useDeleteCvbbMutation,
  useCvbbPContractsLookupQuery,
  useLazyCvbbPContractsLookupQuery,
  useCvbbSContractsLookupQuery,
  useLazyCvbbSContractsLookupQuery,
  useCvbbTypesLookupQuery,
  useLazyCvbbInvoicesLookupQuery,
} = procurementCvbbApi
